// Meta Pixel (browser) for the *platform* merchant-acquisition funnel.
//
// This pairs with the server-side Conversions API in the backend
// (src/services/metaCapiService.ts). Both channels report the SAME merchant
// lifecycle events (signup, store created, first product, paid purchase) to the
// SAME web dataset, using a shared, deterministic event_id per real-world action
// so Meta deduplicates the browser + server copies instead of double-counting.
//
// The pixel's job here is to add what CAPI alone cannot: the browser's fbp/fbc
// cookies and automatic advanced matching, which lift match quality.
//
// Set META_PIXEL_ID to the Web dataset (Pixel) ID from Events Manager. While it
// is blank every function below is a silent no-op, so the app runs unchanged.

// "Ecom Admin" dataset owned by the personal ad account (694683015654320), so
// the campaign can optimize on it. Browser-pixel only — this dataset can't run
// CAPI (no owning business = no Conversions API token), so the deduplication
// notes below are currently inert: the backend CAPI still targets the separate
// business-owned dataset (1727671928699032), which this ad account can't access.
const META_PIXEL_ID = "902091825848448";

let scriptLoaded = false;

// Standard Meta base-code loader (fbq bootstrap). Injects fbevents.js once.
const loadPixelScript = () => {
  /* eslint-disable */
  (function (f: any, b: any, e: string, v: string, n?: any, t?: any, s?: any) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = true;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  /* eslint-enable */
};

const fbq = (): ((...args: any[]) => void) | null => {
  const q = (window as any).fbq;
  return typeof q === "function" ? q : null;
};

/** Load the pixel and fire the initial PageView. Call once at app startup. */
export const initMetaPixel = (): void => {
  if (scriptLoaded || !META_PIXEL_ID) return;
  loadPixelScript();
  const q = fbq();
  if (!q) return;
  q("init", META_PIXEL_ID);
  q("track", "PageView");
  scriptLoaded = true;
};

/**
 * Attach advanced-matching identifiers for the current merchant. Meta hashes
 * these in the browser, so we pass raw values. Calling init again updates the
 * matching used by all subsequent events. Safe to call repeatedly / with partial
 * data — pass whatever identifiers are known at the time.
 */
export const setMetaUser = (user: {
  email?: string;
  phone?: string;
  externalId?: string | number;
}): void => {
  if (!META_PIXEL_ID) return;
  const q = fbq();
  if (!q) return;
  const matching: Record<string, string> = {};
  if (user.email) matching.em = user.email.trim().toLowerCase();
  if (user.phone) matching.ph = user.phone.replace(/\D/g, "");
  if (user.externalId != null) matching.external_id = String(user.externalId);
  q("init", META_PIXEL_ID, matching);
};

type MetaStandardEvent = "CompleteRegistration" | "StartTrial" | "Purchase";

/**
 * Fire a standard Meta event. `eventID` MUST match the backend CAPI event id for
 * the same action (e.g. `signup_${entityId}`, `purchase_${purchaseId}`) so the
 * two copies deduplicate.
 */
export const trackMetaStandard = (
  event: MetaStandardEvent,
  eventID: string,
  data?: Record<string, unknown>
): void => {
  const q = fbq();
  if (!q || !scriptLoaded) return;
  q("track", event, data || {}, { eventID });
};

/** Fire a custom Meta event (e.g. ProductAdded). Same eventID dedup rule applies. */
export const trackMetaCustom = (
  event: string,
  eventID: string,
  data?: Record<string, unknown>
): void => {
  const q = fbq();
  if (!q || !scriptLoaded) return;
  q("trackCustom", event, data || {}, { eventID });
};
